import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid2,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Icons } from "../../../config/MuiIcons.config";
import AppInputField from "../../../components/CommonReusableComponent/AppInputField/AppInputField";
import AppButton from "../../../components/CommonReusableComponent/AppButton/AppButton";
import MUIErrorText from "../../../components/MUIErrorText/MUIErrorText";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createDepartment,
  updateDepartment,
  getAllUsersByPlantIdWithoutSuperAdmin,
} from "../../../helpers/api-functions-helper";
import { useSnackbar } from "../../../contexts/Snackbar.context";
import { useBackdrop } from "../../../contexts/BackDrop.context";
import {
  APP_MESSAGE,
  SNACKBAR_VARIANT,
  FORM_VALIDATIONS,
  USE_FORMIK_SCHEMA,
  ERROR_MESSAGE,
  // ERROR_MESSAGE,
} from "../../../config/Messages.config";
import {
  checkDuplicateEntity,
  getDataForAllOption,
  replaceTextToCaplitalize,
  setRequiredDataForDropdown,
} from "../../../common/CommonFunctions";
import { useMediaQuery } from "@mui/material";
import AppAutoCompleteField from "../../../components/CommonReusableComponent/AppAutoCompleteField/AppAutoCompleteField";
import { getUserRoleAndScreens } from "../../../services/authServices/auth.services";
import { ROLES } from "../../../config/Roles.config";

const CreateNewOrUpdateDepartmentModal = ({
  Open,
  ToggleDialogBox,
  Title,
  DialogData,
  SetDialogData,
  FetchAllDepartmentByBranchId,
  ListOfAllBranches,
  CurrentBranchId,
  AllDepartmentNames,
}) => {
  const { showSnackbar } = useSnackbar();
  const { accountId } = getUserRoleAndScreens();
  // eslint-disable-next-line no-unused-vars
  let isMobileDevice = useMediaQuery("(max-width:500px)");
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const authUser = JSON.parse(
    localStorage.getItem(process.env.REACT_APP_DEV_AUTH_USER)
  );
  const isUpdateMode = DialogData && DialogData?.id;
  const [isCreateOrUpdateClicked, setIsCreateOrUpdateClicked] = useState(false);
  const [usersByPlant, setUsersByPlant] = useState([]);

  // Function used to create new role
  const createNewDepartment = (data) => {
    setIsCreateOrUpdateClicked(true);
    createDepartment(data)
      .then((res) => {
        if (res && res.data) {
          const { statusCode, success, message, data } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data || message || APP_MESSAGE.LOCATION_MASTER.CREATE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.LOCATION_MASTER.CREATE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleResetDialogBox();
            if (CurrentBranchId !== null) {
              FetchAllDepartmentByBranchId(CurrentBranchId);
            }
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.LOCATION_MASTER.CREATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsCreateOrUpdateClicked(false);
      });
  };

  // Function used to update existing role
  const updateExistingDepartment = (data) => {
    setIsCreateOrUpdateClicked(true);
    updateDepartment(data)
      .then((res) => {
        if (res && res.data) {
          const { statusCode, success, data, message } = res.data;
          if (!success && statusCode !== 200) {
            showSnackbar(
              data || message || APP_MESSAGE.LOCATION_MASTER.UPDATE.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
          } else {
            showSnackbar(
              APP_MESSAGE.LOCATION_MASTER.UPDATE.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
            handleResetDialogBox();
            if (CurrentBranchId !== null) {
              FetchAllDepartmentByBranchId(CurrentBranchId);
            }
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.LOCATION_MASTER.UPDATE.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        setIsCreateOrUpdateClicked(false);
      });
  };

  // Fetch all user of a plant/project
  const fetchAllUsersOfBranch = (data) => {
    const payload = {
      branchIds: getDataForAllOption("branch", data, ListOfAllBranches),
      accountId: accountId,
    };
    showBackdrop();
    getAllUsersByPlantIdWithoutSuperAdmin(payload)
      .then((response) => {
        if (response && response.data) {
          const { statusCode, data, message } = response.data;
          if (statusCode && statusCode === 200) {
            const filteredUsers =
              data && data?.length > 0
                ? data.filter((user) => {
                    if (DialogData && DialogData?.id) {
                      // Apply both conditions when department ID exists
                      return (
                        user.roleName.toLowerCase() === ROLES.ADMIN ||
                        (user.roleName.toLowerCase() !== "super admin" &&
                          Array.isArray(user.assignedBranches) && // Ensure assignedBranches exists
                          user.assignedBranches.some(
                            (branch) =>
                              Array.isArray(branch.assignedDepartments) && // Ensure assignedDepartments exists
                              branch.assignedDepartments.some(
                                (department) => department.id === DialogData?.id
                              )
                          ))
                      );
                    } else {
                      // If no department ID, return only users with roleName "Admin"
                      return user.roleName.toLowerCase() === ROLES.ADMIN;
                    }
                  })
                : [];
            // console.log("filteredUsers: ", filteredUsers)
            setRequiredDataForDropdown(setUsersByPlant, filteredUsers, "userName", "id")
            showSnackbar(
              message || data || APP_MESSAGE.LOCATION_MASTER.FETCHED.SUCCESS,
              SNACKBAR_VARIANT.SUCCESS
            );
          } else {
            showSnackbar(
              message || data || APP_MESSAGE.LOCATION_MASTER.FETCHED.FAILED,
              SNACKBAR_VARIANT.ERROR
            );
            setUsersByPlant([]);
          }
        }
      })
      .catch((error) => {
        console.log("Error: ", error);
        showSnackbar(
          APP_MESSAGE.LOCATION_MASTER.FETCHED.FAILED,
          SNACKBAR_VARIANT.ERROR
        );
      })
      .finally(() => {
        hideBackdrop();
      });
  };

  // Formik form validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: (DialogData && DialogData.name) || "",
      abbreviation: (DialogData && DialogData.abbreviation) || "",
      branchId: (DialogData && DialogData.branchId) || "",
      departmentInchargeList:
        (DialogData && DialogData.departmentInchargeList) || [],
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .matches(
          FORM_VALIDATIONS.TEXT_INPUT.REGEX.INITIAL,
          FORM_VALIDATIONS.TEXT_INPUT.MESAAGE.INITIAL
        )
        .required(USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.DEPT_NAME),
      abbreviation: Yup.string()
        .trim()
        .matches(
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.REGEX,
          FORM_VALIDATIONS.LOCATION_NAME_PERFIX.MESSAGE
        )
        .required(USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.ABBRIVATIONS),
      branchId: Yup.string().required(USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.BRANCH),
      departmentInchargeList: Yup.array(
        USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.INCHARGE
      )
        .of(Yup.object().required(USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.INCHARGE))
        .min(1, USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.INCHARGE)
        .required(USE_FORMIK_SCHEMA.DEPARTMENT_MASTER.INCHARGE),
    }),
    onSubmit: (values) => {
      const payload = {
        ...values,
        updatedBy: authUser.uid,
      };
      if (isUpdateMode) {
        const { abbreviation, ...rest } = payload;
        const update_payload = {
          data: {
            ...rest,
          },
          accountId: accountId,
          departmentId: DialogData?.id,
        };
        // console.log("update_payload: ", update_payload);
        updateExistingDepartment(update_payload);
      } else {
        if (checkDuplicateEntity(AllDepartmentNames, values.name)) {
          showSnackbar(
            APP_MESSAGE.LOCATION_MASTER.DUPLICATE_LOCATION_NAME,
            SNACKBAR_VARIANT.ERROR
          );
        } else {
          const create_upload = {
            ...payload,
            createdBy: authUser?.uid,
            abbreviation: payload.abbreviation.trim(),
            name: replaceTextToCaplitalize(payload.name.trim()),
            accountId: accountId,
          };
          // console.log("create_payload: ", create_upload);
          createNewDepartment(create_upload);
        }
      }
    },
  });

  // Function used reset dialog box
  function handleResetDialogBox() {
    ToggleDialogBox("Create/Update");
    validation.handleReset();
    SetDialogData(null);
    setUsersByPlant([]);
  }

  useEffect(() => {
    if (navigator.onLine) {
      if (DialogData) {
        const { branchId } = DialogData;
        fetchAllUsersOfBranch(branchId);
      }
    } else {
      showSnackbar(ERROR_MESSAGE.INTERNET_ERROR, SNACKBAR_VARIANT.ERROR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DialogData]);

  // useEffect(() => {
  //   if (usersByPlant[0] && DialogData) {
  //     const { locationInchargeId, locationInchargeName } = DialogData
  //     validation.setFieldValue("locationInchargeId", locationInchargeId)
  //     setInchargeDetails({
  //       locationInchargeId: locationInchargeId,
  //       locationInchargeName: locationInchargeName,
  //     })
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [DialogData, usersByPlant])

  // Function used handle change in location in-charge
  const handleDepartmentIncharge = (event, value) => {
    const inchargeList = value
      .map((item) => ({
        departmentInchargeName: item.label,
        departmentInchargeId: item.value,
      }))
      .filter(
        (user, index, self) =>
          index ===
          self.findIndex(
            (u) => u.departmentInchargeId === user.departmentInchargeId
          )
      );
    validation.setFieldValue("departmentInchargeList", inchargeList);
  };

  const handleChangeBranch = (event, data) => {
    if (data?.value) {
      const event_ = {
        ...event,
        target: {
          ...event?.target,
          value: data?.value,
          name: "branchId",
          id: "branchId"
        }
      }
      validation.handleChange(event_)
      fetchAllUsersOfBranch(data?.value);
      validation.setFieldValue("departmentInchargeList", []);
      validation.setFieldTouched(
        "departmentInchargeList",
        false,
        false
      );
    }
  }

  return (
    <Dialog
      open={Open}
      disableEscapeKeyDown={true}
      maxWidth="md"
      disableEnforceFocus={true}
      fullWidth
      slotProps={{
        style: {
          opacity: 0.7,
        },
      }}
    >
      <DialogTitle id="responsive-dialog-title">
        <Typography
          className="inter-medium-500"
          style={{
            fontSize: "18px",
          }}
        >
          {Title}
        </Typography>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={handleResetDialogBox}
        style={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Icons.Close />
      </IconButton>
      <DialogContent dividers>
        <div>
          <Grid2 container spacing={2} className="mb-1">
            <Grid2 size={{ xs: 12, md: 4, lg: 4 }}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Department Name
              </Typography>
              <AppInputField
                Name={"name"}
                Id={"name"}
                Type={"text"}
                Disabled={isCreateOrUpdateClicked || isUpdateMode}
                Value={validation.values.name || ""}
                OnBlur={validation.handleBlur}
                OnChange={validation.handleChange}
                IsCapitalizeRequired={true}
                // Label={"Location Name *"}
                PlaceHolder={"Enter department name"}
                Error={
                  validation.touched.name && validation.errors.name
                    ? true
                    : false
                }
                HelperText={
                  validation.touched.name && validation.errors.name ? (
                    <MUIErrorText ErrorMessage={validation.errors.name} />
                  ) : null
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4, lg: 4 }}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Unique Prefix
              </Typography>
              <AppInputField
                Name={"abbreviation"}
                Id={"abbreviation"}
                Type={"text"}
                Disabled={isCreateOrUpdateClicked || isUpdateMode}
                Value={validation.values.abbreviation || ""}
                OnBlur={validation.handleBlur}
                OnChange={(event) => {
                  const { value, name } = event.target;
                  validation.handleChange({
                    ...event,
                    target: {
                      ...event.target,
                      name: name,
                      value: value.toUpperCase(),
                    },
                  });
                }}
                PlaceHolder={"Enter unique prefix"}
                Error={
                  validation.touched.abbreviation &&
                  validation.errors.abbreviation
                    ? true
                    : false
                }
                HelperText={
                  validation.touched.abbreviation &&
                  validation.errors.abbreviation ? (
                    <MUIErrorText
                      ErrorMessage={validation.errors.abbreviation}
                    />
                  ) : null
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 4, lg: 4 }}>
              <Typography variant="body2" className="inter-regular-400 py-2">
                Select Branch
              </Typography>
              {/* <AppInputField
                Name={"branchId"}
                Id={"branchId"}
                IsSelect={true}
                SelectOptions={ListOfAllBranches || []}
                Disabled={isCreateOrUpdateClicked || isUpdateMode}
                Value={validation.values.branchId || ""}
                OnBlur={validation.handleBlur}
                OnChange={(event) => {
                  if (navigator.onLine) {
                    validation.handleChange(event);
                    const { value } = event.target;
                    fetchAllUsersOfBranch(value);
                    validation.setFieldValue("departmentInchargeList", []);
                    validation.setFieldTouched(
                      "departmentInchargeList",
                      false,
                      false
                    );
                  } else {
                    showSnackbar(
                      ERROR_MESSAGE.INTERNET_ERROR,
                      SNACKBAR_VARIANT.ERROR
                    );
                  }
                }}
                // Label={"Select Plant"}
                PlaceHolder={"Select branch"}
                Error={
                  validation.touched.branchId && validation.errors.branchId
                    ? true
                    : false
                }
                HelperText={validation.errors.branchId}
              /> */}
              <AppAutoCompleteField 
                IsMultiple={false}
                TextInputName={"branchId"}
                TextInputId={"branchId"}
                Id={"branchId"}
                TextInputPlaceholder={"Select branch"}
                Options={ListOfAllBranches?.filter(br => br.label !== "All") || []}
                Disabled={isCreateOrUpdateClicked || isUpdateMode}
                Value={validation.values.branchId || ""}
                HandleOnBlur={validation.handleBlur}
                HandleOnChange={(event, value) => {
                  handleChangeBranch(event, value)
                }}
                TextInputError={
                  validation.touched.branchId &&
                  validation.errors.branchId
                    ? true
                    : false
                }
                TextInputHelperText={
                  validation.touched.branchId && validation.errors.branchId
                    ? validation.errors.branchId
                    : null
                }
              />
            </Grid2>
            <Grid2 size={{ xs: 12 }} className="my-2">
              <Typography variant="body2" className="inter-regular-400 py-2">
                Select Department Incharge
              </Typography>
              <AppAutoCompleteField
                Options={usersByPlant || []}
                DisableCloseOnSelect={true}
                ChipLimit={isMobileDevice ? 2 : 6}
                Disabled={isCreateOrUpdateClicked}
                Id={"departmentInchargeList"}
                Value={validation.values.departmentInchargeList.map((user) => ({
                  label: user.departmentInchargeName,
                  value: user.departmentInchargeId,
                }))}
                HandleOnChange={(event, value) =>
                  handleDepartmentIncharge(event, value)
                }
                HandleOnBlur={validation.handleBlur}
                TextInputId={"departmentInchargeList"}
                TextInputName={"departmentInchargeList"}
                TextInputPlaceholder={"Select department incharge"}
                TextInputError={
                  validation.touched.departmentInchargeList &&
                  validation.errors.departmentInchargeList
                    ? true
                    : false
                }
                TextInputHelperText={
                  validation.touched.departmentInchargeList &&
                  validation.errors.departmentInchargeList ? (
                    <MUIErrorText
                      ErrorMessage={validation.errors.departmentInchargeList}
                    />
                  ) : null
                }
              />
            </Grid2>
          </Grid2>
          <Grid2 container spacing={2} className="mt-3">
            {!isUpdateMode ? (
              <Grid2 size={{ xs: 6 }}>
                <AppButton
                  Type={"reset"}
                  IsDisabled={isCreateOrUpdateClicked}
                  OnPress={validation.handleReset}
                  children={"Reset"}
                  Color={"error"}
                />
              </Grid2>
            ) : null}
            <Grid2 size={{ xs: isUpdateMode ? 12 : 6 }}>
              <AppButton
                Type={"submit"}
                OnPress={() => {
                  if (navigator.onLine) {
                    validation.handleSubmit();
                  } else {
                    showSnackbar(
                      ERROR_MESSAGE.INTERNET_ERROR,
                      SNACKBAR_VARIANT.ERROR
                    );
                  }
                }}
                IsDisabled={isCreateOrUpdateClicked}
                LoadingState={isCreateOrUpdateClicked}
              >
                {DialogData !== null ? "Update" : "Create"}
              </AppButton>
            </Grid2>
          </Grid2>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateNewOrUpdateDepartmentModal;
